import { CheckCircle2, History, Merge } from "lucide-react";
import { useState } from "react";
import { Badge, Button, Card, EmptyState, Spinner } from "@/components/common";
import { useClientMerges, useDuplicateClients } from "@/hooks/useClients";
import type {
  DuplicateClient,
  DuplicatePair,
  DuplicateReason,
  MergeField,
} from "@/types/clients";
import { MergeClientsModal } from "./MergeClientsModal";
import { formatDateTime } from "./RodoStatusBadges";

const REASON_LABELS: Record<DuplicateReason, string> = {
  samePesel: "ten sam PESEL",
  sameNip: "ten sam NIP",
  similarName: "podobne imię i nazwisko",
  sameBirthDate: "ta sama data urodzenia",
  samePhone: "ten sam telefon",
  sameEmail: "ten sam e-mail",
};

const FIELD_LABELS: Record<MergeField, string> = {
  firstName: "imię",
  lastName: "nazwisko",
  pesel: "PESEL",
  phone: "telefon",
  email: "e-mail",
  nip: "NIP",
};

interface DuplicateClientsPanelProps {
  /** Administrators: limit to one agent's clients. */
  agentId?: string;
  isAdmin: boolean;
  canMerge: boolean;
}

/** "Possible duplicates" of one agent's clients, with merging and the history of merges. */
export function DuplicateClientsPanel({
  agentId,
  isAdmin,
  canMerge,
}: DuplicateClientsPanelProps) {
  const { data: pairs, isLoading } = useDuplicateClients(agentId);
  const { data: merges } = useClientMerges();
  const [mergePair, setMergePair] = useState<DuplicatePair | null>(null);

  return (
    <div className="space-y-4">
      <Card padding="none">
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : pairs && pairs.length > 0 ? (
          <ul className="divide-y divide-edge-soft">
            {pairs.map((pair) => (
              <li
                key={`${pair.first.client.id}-${pair.second.client.id}`}
                className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center"
              >
                <div className="grid flex-1 gap-3 sm:grid-cols-2">
                  <DuplicateRecord entry={pair.first} isAdmin={isAdmin} />
                  <DuplicateRecord entry={pair.second} isAdmin={isAdmin} />
                </div>
                <div className="flex flex-wrap gap-1 lg:w-56">
                  {pair.reasons.map((r) => (
                    <Badge
                      key={r}
                      variant={
                        r === "samePesel" || r === "sameNip"
                          ? "warning"
                          : "default"
                      }
                    >
                      {REASON_LABELS[r]}
                    </Badge>
                  ))}
                </div>
                {canMerge && (
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Merge size={14} aria-hidden />}
                    onClick={() => {
                      setMergePair(pair);
                    }}
                  >
                    Połącz…
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<CheckCircle2 size={32} />}
            title="Brak możliwych duplikatów"
            description="Nie znaleźliśmy rekordów, które wyglądają na tego samego klienta."
          />
        )}
      </Card>

      {merges && merges.length > 0 && (
        <Card padding="sm">
          <h3 className="mb-2 flex items-center gap-2 text-sm font-medium text-main">
            <History size={16} aria-hidden /> Historia łączeń
          </h3>
          <ul className="divide-y divide-edge-soft text-sm">
            {merges.map((m) => (
              <li key={m.id} className="py-2">
                <p className="text-main">
                  {m.targetName ?? "rekord połączony później"}{" "}
                  <span className="text-sub">← {m.sourceSummary}</span>
                </p>
                <p className="text-xs text-faint">
                  {formatDateTime(m.mergedAt)}
                  {m.mergedBy && ` · ${m.mergedBy}`}
                  {" · z duplikatu: "}
                  {m.fieldsFromSource.length > 0
                    ? m.fieldsFromSource.map((f) => FIELD_LABELS[f]).join(", ")
                    : "nic"}
                  {m.movedConsentRequests > 0 &&
                    ` · wnioski RODO: ${m.movedConsentRequests}`}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <MergeClientsModal
        key={
          mergePair
            ? `${mergePair.first.client.id}-${mergePair.second.client.id}`
            : "none"
        }
        pair={mergePair}
        onClose={() => {
          setMergePair(null);
        }}
      />
    </div>
  );
}

function DuplicateRecord({
  entry,
  isAdmin,
}: {
  entry: DuplicateClient;
  isAdmin: boolean;
}) {
  const c = entry.client;
  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-main">
        {c.firstName} {c.lastName}
      </p>
      <p className="truncate text-xs text-faint">
        PESEL {c.peselMasked} · {c.phoneMasked}
        {c.emailMasked && ` · ${c.emailMasked}`}
        {c.nip && ` · NIP ${c.nip}`}
      </p>
      <p className="text-xs text-faint">
        dodany {formatDateTime(c.createdAt)}
        {isAdmin && ` · ${c.agentName}`}
        {` · RODO ${c.rodo.verified ? "✓" : "—"} · wnioski: ${entry.consentRequestCount}`}
      </p>
    </div>
  );
}
